(() => {
  const galleries = [...document.querySelectorAll('[data-casekoo-standkoo-gallery]')];
  if (!galleries.length) return;

  const lazySelector = 'img[data-casekoo-gallery-lazy]';
  const state = new WeakMap();

  const hydrate = (image, priority = 'low') => {
    if (!(image instanceof HTMLImageElement) || !image.matches(lazySelector)) return Promise.resolve(image);
    if (state.has(image)) return state.get(image);
    const promise = new Promise((resolve) => {
      const done = () => resolve(image);
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
      image.loading = 'eager';
      image.fetchPriority = priority;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.src) image.src = image.dataset.src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
      image.removeAttribute('data-casekoo-gallery-lazy');
      if (image.complete) resolve(image);
    });
    state.set(image, promise);
    return promise;
  };

  const hydrateWithin = (element, priority = 'low') => {
    if (!(element instanceof Element)) return Promise.resolve();
    const images = [];
    if (element.matches(lazySelector)) images.push(element);
    images.push(...element.querySelectorAll(lazySelector));
    return Promise.all(images.map((image) => hydrate(image, priority)));
  };

  galleries.forEach((gallery) => {
    if (gallery.dataset.casekooStandkooLazyReady === 'true') return;
    gallery.dataset.casekooStandkooLazyReady = 'true';
    const section = gallery.closest('.shopify-section') || gallery.parentElement;
    let generation = 0;
    let idleId = 0;

    const slides = () => [...gallery.querySelectorAll('.feature-media-item')];
    const currentSlide = () =>
      gallery.querySelector('.feature-media-item.is-active, .feature-media-item[aria-hidden="false"]')
      || slides().find((slide) => getComputedStyle(slide).display !== 'none')
      || slides()[0];

    const hydrateThumbnails = (mediaId, priority = 'low') => {
      if (mediaId == null || !section) return;
      const escapedId = CSS.escape(String(mediaId));
      section.querySelectorAll('.product-thumbnail-list-item[data-media-id="' + escapedId + '"]').forEach((thumbnail) => {
        hydrateWithin(thumbnail, priority);
      });
    };

    const hydrateTarget = async (slide, token, priority = 'high') => {
      if (!(slide instanceof Element) || token !== generation) return;
      await hydrateWithin(slide, priority);
      if (token !== generation) return;
    };

    const scheduleCurrentAndNext = (mediaId = null) => {
      generation += 1;
      const token = generation;
      const escapedId = mediaId == null ? null : CSS.escape(String(mediaId));
      const slide = escapedId
        ? gallery.querySelector('.feature-media-item[data-media-id="' + escapedId + '"]')
        : currentSlide();
      const target = slide || currentSlide();
      hydrateTarget(target, token, 'high');
      hydrateThumbnails(mediaId ?? target?.dataset.mediaId, 'low');
      const neighbor = target?.nextElementSibling;
      const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 180));
      if (window.cancelIdleCallback && idleId) window.cancelIdleCallback(idleId);
      else if (idleId) window.clearTimeout(idleId);
      idleId = idle(() => {
        if (token === generation) hydrateWithin(neighbor, 'low');
      }, { timeout: 700 });
    };

    const onVariantChange = (event) => {
      const mediaId = event.detail?.variant?.featured_media?.id;
      scheduleCurrentAndNext(mediaId);
    };

    section?.addEventListener('shapes:product:variantchange', onVariantChange, true);
    section?.addEventListener('theme:variant:change', onVariantChange, true);
    section?.addEventListener('mediaVisible', (event) => {
      const slide = event.target instanceof Element ? event.target.closest('.feature-media-item') : null;
      generation += 1;
      hydrateTarget(slide || currentSlide(), generation, 'high');
      hydrateThumbnails(slide?.dataset.mediaId, 'low');
    }, true);

    const primeFromInteraction = () => queueMicrotask(() => scheduleCurrentAndNext());
    section?.addEventListener('pointerdown', primeFromInteraction, { capture: true, passive: true });
    section?.addEventListener('touchstart', primeFromInteraction, { capture: true, passive: true });
    section?.addEventListener('focusin', primeFromInteraction, true);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydrate(entry.target, 'low');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '250px 0px', threshold: 0.01 });
      window.setTimeout(() => {
        section?.querySelectorAll('.product-thumbnail-list-item ' + lazySelector).forEach((image) => observer.observe(image));
      }, 400);
    }

    scheduleCurrentAndNext();
  });
})();
